import {useFluxStore} from 'client/framework';
import React from 'react';

import {Heading, Skeleton} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {AdaptiveSubjectCourseCard} from 'client/SubjectLibrary/AdaptiveSubjectCourseCard/AdaptiveSubjectCourseCard';
import {SubjectsListCard} from 'client/Classrooms/Classroom/Teacher/Subjects/SubjectsListCard.react';
import {SubjectsFilterMenu} from 'client/TeacherDashboard/ClassDetail/Subjects/SubjectsFilterMenu';
import {SubjectsEmptyState} from 'client/TeacherDashboard/components/SubjectsEmptyState';
import SubjectCourseCard from 'client/generic/SubjectMenuRenderer/SubjectCourseCard/SubjectCourseCard';
import type {SubjectCardData} from 'client/generic/SubjectMenuRenderer/types';

import {ErrorRetryState} from '../components/ErrorRetryState';
import {useClassworkSubjects} from './useClassworkSubjects';

// Matches the loaded layout (heading + 168px subject-card grid) so the reveal fills in place.
const ClassworkSubjectsSkeleton = () => (
  <div aria-busy='true' className='gap-x3 pt-x4 flex flex-col'>
    <span className='sr-only' role='status'>
      Loading subjects…
    </span>
    <Heading as='h2' className='m-x0' size='s'>
      Subjects
    </Heading>
    <div aria-hidden='true' className='s:grid-cols-2 m:grid-cols-3 gap-x3 grid grid-cols-1'>
      {['c1', 'c2', 'c3', 'c4', 'c5', 'c6'].map((key) => (
        <Skeleton className='rounded-x3 h-[168px] w-full' key={key} />
      ))}
    </div>
  </div>
);

// v2 all-classwork Subjects view: the subjects used across every class, each with the course-library
// Practice Settings dropdown. A Flux-free port of SubjectLibrary/ClassSubjects, rendered in the v2
// classwork chrome instead of the legacy SubjectMenuRenderer.
export const ClassworkSubjects = () => {
  const {
    isReady,
    hasError,
    reload,
    hasNoSubjects,
    subjects,
    activeClassrooms,
    hasHiddenSubjects,
    showHidden,
    setShowHidden,
    isUserLicensedTeacher
  } = useClassworkSubjects();
  const isLoggedOutOrStudent = useFluxStore(
    sessionStore,
    (s) => !s.hasValidSession() || s.isStudent()
  );

  if (hasError) {
    return (
      <div className='pt-x4'>
        <ErrorRetryState
          copy='We couldn’t load your class subjects.'
          subtitle='Something went wrong. Check your connection and try again.'
          onRetry={reload}
        />
      </div>
    );
  }

  if (!isReady) {
    return <ClassworkSubjectsSkeleton />;
  }

  // All-hidden isn't empty: keep the grid (and its Filters toggle) so hidden subjects stay reachable.
  if (subjects.length === 0 && !hasHiddenSubjects) {
    // Empty from a stale cache while the revisit refetch is in flight — don't assert emptiness yet.
    return hasNoSubjects ? <SubjectsEmptyState /> : <ClassworkSubjectsSkeleton />;
  }

  return (
    <div className='gap-x3 pt-x4 flex flex-col'>
      <Heading as='h2' className='m-x0' size='s'>
        Subjects
      </Heading>
      {hasHiddenSubjects && (
        <div className='flex'>
          <SubjectsFilterMenu showHidden={showHidden} onToggle={setShowHidden} />
        </div>
      )}
      <div className='gap-x3 s:grid-cols-2 m:grid-cols-3 grid grid-cols-1'>
        {subjects.map((subject) => {
          if (subject.isHidden()) {
            return (
              <SubjectsListCard
                key={subject.getId()}
                subject={subject}
                isAdaptive={subject.isAdaptive()}
                isHidden
                showActions={false}
              />
            );
          }
          if (subject.isAdaptive()) {
            return (
              <AdaptiveSubjectCourseCard
                key={subject.getId()}
                subjectId={subject.getId()}
                subjectName={subject.getName()}
                subjectUrlSlug={subject.getUrlSlug()}
                subjectDescription={subject.getDescription() || ''}
                curriculumAreaName={subject.getCurriculumArea().getName() || ''}
                isLoggedOutOrStudent={isLoggedOutOrStudent}
                data-testid={subject.getName()}
              />
            );
          }
          const cardData: SubjectCardData = {
            id: subject.getId(),
            name: subject.getName(),
            description: subject.getDescription(),
            urlSlug: subject.getUrlSlug(),
            curriculumAreaSlug: subject.getCurriculumAreaSlug(),
            logoHref: subject.getLogoLocation().href
          };
          return (
            <SubjectCourseCard
              key={subject.getId()}
              subject={cardData}
              to={`/${subject.getUrlSlug()}`}
              content={subject.getDescription()}
              isAssessmentsOnly={subject
                .getGuides()
                .every((guide) => guide.getGuideType() === 'assessment')}
              isUserLicensedTeacher={isUserLicensedTeacher}
              isLoggedOutOrStudent={isLoggedOutOrStudent}
              activeClassrooms={activeClassrooms}
            />
          );
        })}
      </div>
    </div>
  );
};
